// Standalone client self-screening test pathway (ZO-11165).
//
// A logged-in CLIENT runs a patient-screening flow against the screening
// service. Because the client's own session token is sent (see
// screening.service.js), the evaluation attributes to the client — the
// production-faithful path that the WA3 operator demo and the (now token-less)
// patient-screening-web tester cannot exercise on the Grails-auth dev backend.
//
// Reuses the existing email/password client login (clientLogin) — no new auth.
import React, { useCallback, useState } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  MenuItem,
  CircularProgress,
  Divider,
  Chip,
  makeStyles,
} from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import { clientLogin, isAuthenticated } from "services/authentication.service";
import { ScreeningService } from "services/screening.service";

const useStyles = makeStyles((theme) => ({
  appBar: { backgroundColor: "#74BACD" },
  container: { marginTop: 96, marginBottom: 48 },
  paper: { padding: theme.spacing(4), borderRadius: 16, maxWidth: 720, margin: "0 auto" },
  field: { marginBottom: theme.spacing(2) },
  actions: { marginTop: theme.spacing(2), display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
  devBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    border: "1px solid #f0c36d",
    background: "#fff8e6",
    borderRadius: 8,
  },
  q: { marginBottom: theme.spacing(2) },
}));

// ── show_if evaluator (mirrors patient-screening-web / WA3) ────────────────
function asNumber(v) {
  return typeof v === "number" ? v : Number(v);
}
function evalFieldOp(actual, opObj) {
  return Object.entries(opObj).every(([op, expected]) => {
    switch (op) {
      case "eq": return actual === expected;
      case "neq": return actual !== expected;
      case "is_true": return actual === true;
      case "is_false": return actual === false;
      case "includes": return Array.isArray(actual) && actual.includes(expected);
      case "includes_any":
        return Array.isArray(actual) && Array.isArray(expected) && expected.some((e) => actual.includes(e));
      case "between": {
        if (!Array.isArray(expected) || actual === undefined || actual === null || actual === "") return false;
        const n = asNumber(actual);
        return !Number.isNaN(n) && n >= asNumber(expected[0]) && n <= asNumber(expected[1]);
      }
      case "gt": return !Number.isNaN(asNumber(actual)) && asNumber(actual) > asNumber(expected);
      case "gte": return !Number.isNaN(asNumber(actual)) && asNumber(actual) >= asNumber(expected);
      case "lt": return !Number.isNaN(asNumber(actual)) && asNumber(actual) < asNumber(expected);
      case "lte": return !Number.isNaN(asNumber(actual)) && asNumber(actual) <= asNumber(expected);
      default: return true;
    }
  });
}
function evalShowIf(cond, answers) {
  if (!cond || typeof cond !== "object") return true;
  if (Array.isArray(cond.all_of)) return cond.all_of.every((s) => evalShowIf(s, answers));
  if (Array.isArray(cond.any_of)) return cond.any_of.some((s) => evalShowIf(s, answers));
  if ("not" in cond) return !evalShowIf(cond.not, answers);
  return Object.entries(cond).every(([field, opObj]) =>
    opObj && typeof opObj === "object" ? evalFieldOp(answers[field], opObj) : true,
  );
}
function isVisible(q, answers) {
  return q.show_if === undefined || q.show_if === null || evalShowIf(q.show_if, answers);
}
function qLabel(q) {
  return q.label?.en || q.code;
}
function optLabel(o) {
  return o.label?.en || o.value;
}

function deviceId() {
  let d = localStorage.getItem("screening_device_id");
  if (!d) {
    d = uuidv4();
    localStorage.setItem("screening_device_id", d);
  }
  return d;
}

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
}

const ClientScreening = () => {
  const classes = useStyles();
  const [authed, setAuthed] = useState(isAuthenticated());
  const [user, setUser] = useState(currentUser());

  // login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // screening
  const [serviceCode, setServiceCode] = useState("nad");
  const [template, setTemplate] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [evaluation, setEvaluation] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetMsg, setResetMsg] = useState(null);

  const errMsg = (e, fallback) => e?.body?.detail || e?.message || fallback;

  const handleLogin = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await clientLogin({ email: email.trim(), password, fingerprint: deviceId() });
      setUser(currentUser());
      setAuthed(true);
    } catch (e) {
      setError(errMsg(e, "Login failed — check email / password."));
    } finally {
      setLoading(false);
    }
  }, [email, password]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("user");
    setAuthed(false);
    setUser(null);
    setTemplate(null);
    setSession(null);
    setEvaluation(null);
    setAnswers({});
  }, []);

  const handleStart = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResetMsg(null);
    setEvaluation(null);
    setAnswers({});
    try {
      const tpl = await ScreeningService.resolveTemplate(serviceCode);
      if (!tpl) {
        setTemplate(null);
        setSession(null);
        setError(`No screening template published for service_code "${serviceCode}".`);
        return;
      }
      const newSession = await ScreeningService.createSession({
        template_key: tpl.template_key,
        service_code: serviceCode,
        source_app: "web",
      });
      setTemplate(tpl);
      setSession(newSession);
    } catch (e) {
      setError(errMsg(e, "Failed to start screening."));
    } finally {
      setLoading(false);
    }
  }, [serviceCode]);

  const setAnswer = useCallback((code, value) => {
    setAnswers((prev) => ({ ...prev, [code]: value }));
  }, []);

  const handleEvaluate = useCallback(async () => {
    if (!template) return;
    setLoading(true);
    setError(null);
    try {
      // Submit only visible, non-empty answers.
      const byCode = new Map((template.questions ?? []).map((q) => [q.code, q]));
      const payload = {};
      Object.entries(answers).forEach(([code, val]) => {
        if (val === undefined || val === null || val === "") return;
        const q = byCode.get(code);
        if (q && !isVisible(q, answers)) return;
        payload[code] = val;
      });
      // A sealed evaluation is never mutated — re-evaluating spins a new session.
      let sess = session;
      if (!sess || evaluation) {
        sess = await ScreeningService.createSession({
          template_key: template.template_key,
          service_code: serviceCode,
          source_app: "web",
        });
        setSession(sess);
      }
      await ScreeningService.upsertAnswers(sess.id, payload);
      const ev = await ScreeningService.evaluate(sess.id);
      setEvaluation(ev);
    } catch (e) {
      setError(errMsg(e, "Evaluation failed."));
    } finally {
      setLoading(false);
    }
  }, [template, answers, session, evaluation, serviceCode]);

  const handleReset = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResetMsg(null);
    try {
      const result = await ScreeningService.resetScreening(serviceCode);
      setEvaluation(null);
      setSession(null);
      setTemplate(null);
      setAnswers({});
      setResetMsg(
        `Cleared ${result?.deleted?.evaluations ?? 0} screening evaluation(s) for your account — start fresh below.`,
      );
    } catch (e) {
      setError(errMsg(e, "Reset failed (is ENABLE_TEST_RESET on for this env?)."));
    } finally {
      setLoading(false);
    }
  }, [serviceCode]);

  const renderQuestion = (q) => {
    const value = answers[q.code];
    if (q.type === "attestation") {
      return (
        <FormControlLabel
          key={q.code}
          className={classes.q}
          control={<Checkbox checked={value === true} onChange={(e) => setAnswer(q.code, e.target.checked)} />}
          label={qLabel(q)}
        />
      );
    }
    if (q.type === "boolean") {
      return (
        <FormControl key={q.code} component="fieldset" className={classes.q} fullWidth>
          <FormLabel component="legend">{qLabel(q)}</FormLabel>
          <RadioGroup
            row
            value={value === undefined || value === null ? "" : String(value)}
            onChange={(e) => setAnswer(q.code, e.target.value === "true")}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      );
    }
    if (q.type === "single_select" && q.options) {
      return (
        <TextField
          key={q.code}
          select
          fullWidth
          variant="outlined"
          className={classes.q}
          label={qLabel(q)}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => setAnswer(q.code, e.target.value)}
        >
          {q.options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {optLabel(o)}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (q.type === "multi_select" && q.options) {
      const selected = Array.isArray(value) ? value : [];
      const toggle = (v) =>
        setAnswer(q.code, selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
      return (
        <FormControl key={q.code} component="fieldset" className={classes.q} fullWidth>
          <FormLabel component="legend">{qLabel(q)}</FormLabel>
          {q.options.map((o) => (
            <FormControlLabel
              key={o.value}
              control={<Checkbox checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />}
              label={optLabel(o)}
            />
          ))}
        </FormControl>
      );
    }
    if (q.type === "number") {
      return (
        <TextField
          key={q.code}
          type="number"
          fullWidth
          variant="outlined"
          className={classes.q}
          label={qLabel(q)}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => setAnswer(q.code, e.target.value === "" ? undefined : Number(e.target.value))}
        />
      );
    }
    return (
      <TextField
        key={q.code}
        fullWidth
        variant="outlined"
        className={classes.q}
        label={qLabel(q)}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => setAnswer(q.code, e.target.value)}
      />
    );
  };

  const visibleQuestions = (template?.questions ?? [])
    .filter((q) => isVisible(q, answers))
    .sort((a, b) => (a.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.sort_order ?? Number.MAX_SAFE_INTEGER));

  return (
    <div>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            Client screening (test)
          </Typography>
          {authed && (
            <Button color="inherit" onClick={handleLogout}>
              Log out
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container className={classes.container}>
        <Paper className={classes.paper} elevation={2}>
          {!authed ? (
            <>
              <Typography variant="h6" gutterBottom>
                Log in as a client
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Screening you run here is attributed to this client account (the authenticated
                caller), so it mirrors a real patient self-screening.
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                className={classes.field}
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                fullWidth
                variant="outlined"
                type="password"
                className={classes.field}
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || !email || !password}
                  onClick={handleLogin}
                >
                  {loading ? "Signing in…" : "Log in"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary">
                Logged in as{" "}
                <strong>{user?.client?.email || user?.user?.email || `client #${user?.client?.id}`}</strong>
              </Typography>

              <div className={classes.actions}>
                <TextField
                  variant="outlined"
                  size="small"
                  label="service_code"
                  value={serviceCode}
                  onChange={(e) => setServiceCode(e.target.value)}
                />
                <Button variant="contained" color="primary" disabled={loading} onClick={handleStart}>
                  {loading ? "Starting…" : "Resolve & start screening"}
                </Button>
              </div>

              {template && session && (
                <>
                  <Divider style={{ margin: "24px 0" }} />
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Template <code>{template.template_key}</code> v{template.version} · session{" "}
                    <code>{session.id}</code>
                  </Typography>
                  {visibleQuestions.map(renderQuestion)}
                  {visibleQuestions.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      Template returned no questions — you can still evaluate.
                    </Typography>
                  )}
                  <div className={classes.actions}>
                    <Button variant="contained" color="primary" disabled={loading} onClick={handleEvaluate}>
                      {loading ? "Evaluating…" : "Evaluate screening"}
                    </Button>
                  </div>
                </>
              )}

              {evaluation && (
                <>
                  <Divider style={{ margin: "24px 0" }} />
                  <Typography variant="h6" gutterBottom>
                    Outcome
                  </Typography>
                  <Chip
                    label={(evaluation.outcome || "unknown").toUpperCase()}
                    color={evaluation.outcome === "pass" ? "primary" : "default"}
                    style={{ marginBottom: 12 }}
                  />
                  <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                    Evaluation <code>{evaluation.id}</code> · user{" "}
                    <code>{evaluation.zennya_user_id}</code> · profile{" "}
                    <code>{evaluation.request_profile_id}</code>
                  </Typography>
                  {(evaluation.outcome_reasons || []).length > 0 && (
                    <ul>
                      {evaluation.outcome_reasons.map((r) => (
                        <li key={r}>
                          <Typography variant="body2">{r}</Typography>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              <div className={classes.devBox}>
                <Typography variant="subtitle2">Dev tools — reset my screening</Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                  Clears your own screening evaluations for <code>{serviceCode}</code> so you can
                  re-run (re-screening alone no longer clears a persisted hard-block). Gated
                  server-side by ENABLE_TEST_RESET.
                </Typography>
                <Button variant="outlined" color="secondary" disabled={loading} onClick={handleReset}>
                  {loading ? "Resetting…" : "Reset screening"}
                </Button>
                {resetMsg && (
                  <Typography variant="caption" style={{ display: "block", marginTop: 8, color: "#2e7d32" }}>
                    {resetMsg}
                  </Typography>
                )}
              </div>
            </>
          )}

          {error && (
            <Typography variant="body2" style={{ color: "#c62828", marginTop: 16 }}>
              {error}
            </Typography>
          )}
          {loading && <CircularProgress size={20} style={{ marginTop: 16 }} />}
        </Paper>
      </Container>
    </div>
  );
};

export default ClientScreening;
